import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import SingleEvent from '../classes/events/SingleEvent';
import Loading from '../components/layout/Loading';
import { getProfileCardDisplayName } from '../utils/profileDisplayName';
import './EventInvitePage.css';

function EventInvitePage({ eventId, onAuthSuccess }) {
  const { t } = useTranslation(['events', 'shared']);
  const [event, setEvent] = useState(null);
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
      
      // Jeśli użytkownik jest zalogowany, przekieruj do głównej aplikacji
      // InviteHandler w App.js otworzy popup automatycznie
      if (session && eventId) {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsLoggedIn(false);
    }
  }, [eventId]);

  const loadEventData = useCallback(async () => {
    try {
      setLoading(true);
      const eventResult = await SingleEvent.loadEvent(eventId);
      
      if (eventResult.error || !eventResult.data) {
        setEvent(null);
        return;
      }

      setEvent(eventResult.data);

      // Load host
      if (eventResult.data.host_id) {
        const hostResult = await SingleEvent.loadHost(eventResult.data.host_id);
        if (hostResult.data) {
          setHost(hostResult.data);
        }
      }
    } catch (error) {
      console.error('Error loading event:', error);
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    // Zapisz eventId w sessionStorage dla późniejszego użycia po zalogowaniu
    if (eventId) {
      sessionStorage.setItem('pendingEventInvite', eventId);
    }

    checkAuth();
    loadEventData();
  }, [eventId, checkAuth, loadEventData]);

  const handleLogin = () => {
    // EventId jest już zapisany w sessionStorage
    // Przekieruj do LandingPage - po zalogowaniu InviteHandler otworzy popup
    window.location.href = '/';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  // Jeśli użytkownik jest zalogowany, przekieruj do głównej aplikacji
  // (popup zostanie otwarty przez InviteHandler w App.js)
  if (isLoggedIn && !loading) {
    // Przekieruj do głównej strony - InviteHandler otworzy popup
    window.location.href = '/';
    return (
      <div className="event-invite-redirect">
        <p>{t('events.invite.redirecting')}</p>
      </div>
    );
  }

  // Jeśli użytkownik nie jest zalogowany, pokaż stronę zaproszenia
  if (loading) {
    return (
      <div className="event-invite-page">
        <div className="event-invite-container">
          <Loading size="medium" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-invite-page">
        <div className="event-invite-container">
          <h1>{t('events.invite.eventNotFound')}</h1>
          <p>{t('events.invite.eventNotFoundDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="event-invite-page">
      <div className="event-invite-container">
        <div className="event-invite-header">
          <h1>{t('events.invite.title')}</h1>
        </div>

        <div className="event-invite-content">
          <div className="event-invite-event-card">
            <h2 className="event-invite-event-title">{event.title}</h2>
            
            <div className="event-invite-event-details">
              <div className="event-invite-detail-item">
                <span className="event-invite-detail-label">{t('events.invite.date')}:</span>
                <span className="event-invite-detail-value">{formatDate(event.event_date)}</span>
              </div>
              
              <div className="event-invite-detail-item">
                <span className="event-invite-detail-label">{t('events.invite.time')}:</span>
                <span className="event-invite-detail-value">{formatTime(event.start_time)}</span>
              </div>

              {host && (
                <div className="event-invite-detail-item">
                  <span className="event-invite-detail-label">{t('events.invite.organizer')}:</span>
                  <span className="event-invite-detail-value">
                    {getProfileCardDisplayName(host, {
                      allowRealName: false,
                      nameNotSetLabel: t('events.details.unknownHost'),
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="event-invite-message">
            <p>{t('events.invite.message')}</p>
          </div>

          <div className="event-invite-actions">
            <button onClick={handleLogin} className="event-invite-button event-invite-button-primary">
              {t('events.invite.loginToJoin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventInvitePage;
